import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, Tooltip } from 'recharts';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Info } from 'lucide-react';
import { useDashboard } from '@/contexts/DashboardContext';
import { WidgetData } from '@/types';
import { WidgetTitle } from "@/components/widgets/WidgetTitle";

type XAxisRangeType = 'hour' | 'day' | 'monthly' | 'year';

// Same grouping type + rule as CallVolumeGroupWidget (src/components/widgets/call-overview/CallVolumeGroupWidget.tsx),
// ported verbatim so both Key Metrics charts tick/format identically for the same data.
type GroupByType = "year" | "month" | "day" | "hour" | "daydiffyear";

const getGroupByFromDateRange = (dateRange?: { from: Date; to: Date }): GroupByType => {
  if (!dateRange) return "day";
  const thisYear = new Date().getFullYear();
  const isDateRangeTheSameYear = dateRange.from.getFullYear() === thisYear && dateRange.to.getFullYear() === thisYear;
  const isDateRangeTheSameMonth = dateRange.from.getMonth() === dateRange.to.getMonth() && isDateRangeTheSameYear;
  const isDateRangeTheSameDay = dateRange.from.getDate() === dateRange.to.getDate() && isDateRangeTheSameMonth && isDateRangeTheSameYear;

  if (isDateRangeTheSameDay) {
    return "hour";
  } else if (isDateRangeTheSameYear) {
    return "day";
  } else {
    return "daydiffyear";
  }
};

const formatXAxisTick = (value: Date, groupBy: GroupByType): string => {
  if (groupBy === "hour") {
    return value.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } else if (groupBy === "daydiffyear") {
    return value.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  }
  return value.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
};

const formatTooltipTime = (time: Date, groupBy: GroupByType): string => {
  if (groupBy === "hour") {
    return time.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }
  return time.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export interface CallStatusDistributionPoint {
  time: string;
  inboundMissed: number;
  inboundHandled: number;
  outboundMissed: number;
  outboundHandled: number;
  internalCall: number;
  inboundDirectHandled: number;
  inboundDirectMissed: number;
}

interface ProcessedPoint extends Omit<CallStatusDistributionPoint, 'time'> {
  time: Date;
}

interface CallStatusDistributionTrendWidgetProps {
  data: WidgetData;
  loading?: boolean;
  error?: string;
  rangeType?: XAxisRangeType;
  dateRange?: { from: Date; to: Date };
}

// Build placeholder buckets (0 calls) spanning dateRange so axes still render when there's no data
const generateEmptyBuckets = (dateRange: { from: Date; to: Date }, groupBy: GroupByType): ProcessedPoint[] => {
  const buckets: ProcessedPoint[] = [];
  const cursor = new Date(dateRange.from);
  const zeroPoint = {
    inboundMissed: 0,
    inboundHandled: 0,
    outboundMissed: 0,
    outboundHandled: 0,
    internalCall: 0,
    inboundDirectHandled: 0,
    inboundDirectMissed: 0,
  };

  if (groupBy === 'hour') {
    cursor.setMinutes(0, 0, 0);
    for (let i = 0; i < 24; i++) {
      buckets.push({ time: new Date(cursor), ...zeroPoint });
      cursor.setHours(cursor.getHours() + 1);
    }
    return buckets;
  }

  cursor.setHours(0, 0, 0, 0);
  const end = new Date(dateRange.to);
  end.setHours(0, 0, 0, 0);
  while (cursor.getTime() <= end.getTime()) {
    buckets.push({ time: new Date(cursor), ...zeroPoint });
    cursor.setDate(cursor.getDate() + 1);
  }
  return buckets;
};

const getAreaColors = (theme: string) => ({
  inboundMissed: theme === 'dark' ? '#ef4444' : '#f87171',
  inboundHandled: theme === 'dark' ? '#60a5fa' : '#2563eb',
  outboundMissed: theme === 'dark' ? '#fbbf24' : '#fbbf24',
  outboundHandled: theme === 'dark' ? '#38bdf8' : '#22d3ee',
  internalCall: theme === 'dark' ? '#8E44AD' : '#8E44AD',
  inboundDirectHandled: theme === 'dark' ? '#10b981' : '#10b981',
  inboundDirectMissed: theme === 'dark' ? '#ec4899' : '#ec4899',
});

const areaNames = {
  inboundMissed: 'Inbound Missed',
  inboundHandled: 'Inbound Handled',
  outboundMissed: 'Outbound Missed',
  outboundHandled: 'Outbound Handled',
  internalCall: 'Internal Calls',
  inboundDirectHandled: 'Inbound Direct Handled',
  inboundDirectMissed: 'Inbound Direct Missed',
} as const;

function computeIsAllZero(processedData: ProcessedPoint[]): boolean {
  if (!processedData.length) return false;
  return processedData.every((d) =>
    (d.inboundMissed ?? 0) === 0 &&
    (d.inboundHandled ?? 0) === 0 &&
    (d.outboundMissed ?? 0) === 0 &&
    (d.outboundHandled ?? 0) === 0 &&
    (d.internalCall ?? 0) === 0 &&
    (d.inboundDirectHandled ?? 0) === 0 &&
    (d.inboundDirectMissed ?? 0) === 0,
  );
}

function computeTickGap(itemCount: number, containerWidth: number, groupBy: GroupByType): number {
  const width = containerWidth - 60 - 25;
  const required = groupBy === 'daydiffyear' ? 80 : 60;
  return Math.ceil(required / (width / itemCount));
}

function computeTicks(processedData: ProcessedPoint[], tickGap: number): Date[] {
  if (processedData.length <= tickGap) return processedData.map((d) => d.time);
  return Array.from(
    { length: Math.ceil(processedData.length / tickGap) },
    (_, i) => processedData[i * tickGap]?.time,
  ).filter((time): time is Date => Boolean(time));
}

type TooltipEntry = { payload: ProcessedPoint };

const ChartTooltip: React.FC<{ active?: boolean; payload?: TooltipEntry[]; label?: Date; theme: string; groupBy: GroupByType }> = ({ active, payload, label, theme, groupBy }) => {
  if (!active || !payload?.length || !label) return null;
  const colors = getAreaColors(theme);
  const row = payload[0]?.payload ?? ({} as ProcessedPoint);
  return (
    <div className={`border rounded-lg shadow-md p-3 min-w-[220px] ${theme === 'dark' ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`} style={{ fontSize: 14 }}>
      <div className="font-semibold">{formatTooltipTime(label, groupBy)}</div>
      {Object.entries(areaNames).map(([key, name]) => (
        <div key={key} className="text-sm" style={{ color: colors[key as keyof typeof colors] }}>
          {name}: {row[key as keyof typeof row] ?? 0}
        </div>
      ))}
    </div>
  );
};

interface TrendLineChartProps {
  processedData: ProcessedPoint[];
  theme: string;
  groupBy: GroupByType;
  colors: ReturnType<typeof getAreaColors>;
  isAllZero: boolean;
  tickGap: number;
}

const TrendLineChart: React.FC<TrendLineChartProps> = ({ processedData, theme, groupBy, colors, isAllZero, tickGap }) => (
  <ResponsiveContainer width="100%" height="100%">
    <LineChart data={processedData} margin={{ left: 0, right: 25, top: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis
        dataKey="time"
        tick={{ fontSize: 14, fill: theme === 'dark' ? '#fff' : '#6b7280' }}
        tickFormatter={(value) => formatXAxisTick(value, groupBy)}
        interval={0}
        ticks={computeTicks(processedData, tickGap)}
      />
      <YAxis
        tick={{ fontSize: 14, fill: theme === 'dark' ? '#fff' : '#6b7280' }}
        domain={isAllZero ? [0, 10] : undefined}
        ticks={isAllZero ? [0, 2, 4, 6, 8, 10] : undefined}
        allowDecimals={false}
      />
      <Tooltip content={<ChartTooltip theme={theme} groupBy={groupBy} />} />
      <Legend verticalAlign="top" align="center" wrapperStyle={{ fontSize: '14px', paddingBottom: '10px' }} />
      {!isAllZero && (
        <>
          <Line type="monotone" dataKey="inboundMissed" name={areaNames.inboundMissed} stroke={colors.inboundMissed} strokeWidth={2} dot={{ r: 4, stroke: colors.inboundMissed, strokeWidth: 2, fill: colors.inboundMissed }} activeDot={{ r: 6, fill: colors.inboundMissed, stroke: colors.inboundMissed, strokeWidth: 2 }} />
          <Line type="monotone" dataKey="inboundHandled" name={areaNames.inboundHandled} stroke={colors.inboundHandled} strokeWidth={2} dot={{ r: 4, stroke: colors.inboundHandled, strokeWidth: 2, fill: colors.inboundHandled }} activeDot={{ r: 6, fill: colors.inboundHandled, stroke: colors.inboundHandled, strokeWidth: 2 }} />
          <Line type="monotone" dataKey="outboundMissed" name={areaNames.outboundMissed} stroke={colors.outboundMissed} strokeWidth={2} dot={{ r: 4, stroke: colors.outboundMissed, strokeWidth: 2, fill: colors.outboundMissed }} activeDot={{ r: 6, fill: colors.outboundMissed, stroke: colors.outboundMissed, strokeWidth: 2 }} />
          <Line type="monotone" dataKey="outboundHandled" name={areaNames.outboundHandled} stroke={colors.outboundHandled} strokeWidth={2} dot={{ r: 4, stroke: colors.outboundHandled, strokeWidth: 2, fill: colors.outboundHandled }} activeDot={{ r: 6, fill: colors.outboundHandled, stroke: colors.outboundHandled, strokeWidth: 2 }} />
          <Line type="monotone" dataKey="internalCall" name={areaNames.internalCall} stroke={colors.internalCall} strokeWidth={2} dot={{ r: 4, stroke: colors.internalCall, strokeWidth: 2, fill: colors.internalCall }} activeDot={{ r: 6, fill: colors.internalCall, stroke: colors.internalCall, strokeWidth: 2 }} />
          <Line type="monotone" dataKey="inboundDirectHandled" name={areaNames.inboundDirectHandled} stroke={colors.inboundDirectHandled} strokeWidth={2} dot={{ r: 4, stroke: colors.inboundDirectHandled, strokeWidth: 2, fill: colors.inboundDirectHandled }} activeDot={{ r: 6, fill: colors.inboundDirectHandled, stroke: colors.inboundDirectHandled, strokeWidth: 2 }} />
          <Line type="monotone" dataKey="inboundDirectMissed" name={areaNames.inboundDirectMissed} stroke={colors.inboundDirectMissed} strokeWidth={2} dot={{ r: 4, stroke: colors.inboundDirectMissed, strokeWidth: 2, fill: colors.inboundDirectMissed }} activeDot={{ r: 6, fill: colors.inboundDirectMissed, stroke: colors.inboundDirectMissed, strokeWidth: 2 }} />
        </>
      )}
    </LineChart>
  </ResponsiveContainer>
);

interface TrendBodyProps {
  loading?: boolean;
  processedData: ProcessedPoint[];
  theme: string;
  groupBy: GroupByType;
  colors: ReturnType<typeof getAreaColors>;
  isAllZero: boolean;
  tickGap: number;
}

const TrendBody: React.FC<TrendBodyProps> = ({ loading, processedData, theme, groupBy, colors, isAllZero, tickGap }) => {
  if (loading) {
    return <Skeleton className="w-full h-full rounded-none" />;
  }
  return (
    <TrendLineChart
      processedData={processedData}
      theme={theme}
      groupBy={groupBy}
      colors={colors}
      isAllZero={isAllZero}
      tickGap={tickGap}
    />
  );
};

const CallStatusDistributionTrendWidget: React.FC<CallStatusDistributionTrendWidgetProps> = ({ data, loading, dateRange: dateRangeProp }) => {
  const { theme } = useDashboard();
  const colors = getAreaColors(theme);

  // Convert to local timezone Date objects, same as CallVolumeGroupWidget's processedData.
  const processedData: ProcessedPoint[] = useMemo(() => {
    return ((data.value as CallStatusDistributionPoint[]) || []).map(item => ({
      ...item,
      time: new Date(item.time),
    }));
  }, [data?.value]);

  const effectiveDateRange = useMemo(() => {
    if (processedData.length) {
      const times = processedData.map(item => item.time.getTime());
      return { from: new Date(Math.min(...times)), to: new Date(Math.max(...times)) };
    }
    return dateRangeProp;
  }, [processedData, dateRangeProp]);

  const groupBy: GroupByType = getGroupByFromDateRange(effectiveDateRange);

  // No data at all: show empty axes (still 0..10 scale) instead of a "no data" message
  let chartData = processedData;
  if (!processedData.length) {
    chartData = effectiveDateRange ? generateEmptyBuckets(effectiveDateRange, groupBy) : [];
  }

  const isAllZero = computeIsAllZero(chartData);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [tickGap, setTickGap] = useState<number>(1);
  useEffect(() => {
    if (containerRef.current && chartData.length) {
      const width = containerRef.current.clientWidth || containerRef.current.offsetWidth || 600;
      setTickGap(computeTickGap(chartData.length, width, groupBy));
    }
  }, [groupBy, chartData]);

  return (
    <div className="w-full min-w-[200px] min-h-[260px] rounded-lg border flex flex-col bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#3C5C73] p-3">
      <div ref={containerRef} className="flex items-center gap-2">
        <WidgetTitle className="truncate">Call Status Distribution Trend</WidgetTitle>
        <TooltipProvider>
          <UITooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="cursor-pointer">
                <Info className="h-4 w-4 text-primary dark:text-white" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="max-w-xs text-xs">The pattern of call status within the given time.</p>
            </TooltipContent>
          </UITooltip>
        </TooltipProvider>
      </div>

      <TrendBody
        loading={loading}
        processedData={chartData}
        theme={theme}
        groupBy={groupBy}
        colors={colors}
        isAllZero={isAllZero}
        tickGap={tickGap}
      />
    </div>
  );
};

export default CallStatusDistributionTrendWidget;


